import moment from "moment-timezone"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import isSameOrBefore from "dayjs/plugin/isSameOrBefore"
import isSameOrAfter from "dayjs/plugin/isSameOrAfter"
import pluralize from "pluralize"
import { t } from "@lingui/macro"

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)

const hasTime = (date) => date && dayjs(date).format("HH:mm") !== "00:00"

const formatTime = (value, timeZone) => (timeZone ? dayjs(value).tz(timeZone) : dayjs(value)).format("h:mm A")
const getDay = (value, timeZone) => (timeZone ? dayjs(value).tz(timeZone) : dayjs(value)).format("dddd")
const formatShortDate = (value, timeZone) => (timeZone ? dayjs(value).tz(timeZone) : dayjs(value)).format("MMM D")
const formatShortTime = (value, timeZone) => (timeZone ? dayjs(value).tz(timeZone) : dayjs(value)).format("h:mmA")
const formatDateTime = (value, timeZone, format = "Do MMM YYYY h:mma z") =>
  (timeZone ? dayjs(value).tz(timeZone) : dayjs(value)).format(format)
const isBeforeToday = (value) => dayjs(value).isBefore(dayjs().startOf("day"))

const scheduleDateToMoment = (value) =>
  value && value.year
    ? moment(`${value.year}-${value.month}-${value.day} ${value.hour}:${value.minute}Z`, "YYYY-M-D H:m")
    : null

const scheduleTimeToMoment = (value) =>
  value && typeof value.hour === "number" && typeof value.minute === "number"
    ? moment().hour(value.hour).minute(value.minute).second(0).millisecond(0)
    : null

const scheduleDatesMatch = (date1, date2) =>
  ["year", "month", "day", "hour", "minute"].every((part) => date1[part] === date2[part])

const formatScheduleDate = (value) => (value ? scheduleDateToMoment(value).format("D MMM YY") : "")
const formatScheduleTime = (value) => (value ? scheduleDateToMoment(value).format("h:mm A") : "")
const momentToScheduleDate = (value) =>
  value
    ? {
        year: value.year(),
        month: value.month() + 1,
        day: value.date(),
        hour: value.hour(),
        minute: value.minute()
      }
    : null

const momentToScheduleTime = (value) =>
  value
    ? {
        hour: value.hour(),
        minute: value.minute()
      }
    : null

// YYYY-MM-DD
const isoDateStringToScheduleDate = (isoDate) => {
  if (!isoDate) {
    return null
  }

  const dateParts = isoDate.split("-")

  if (dateParts.length !== 3) {
    throw new Error(`Invalid ISO date string: ${isoDate}`)
  }

  return {
    year: parseInt(dateParts[0]),
    month: parseInt(dateParts[1]),
    day: parseInt(dateParts[2])
  }
}
const scheduleDateToIsoDateString = (value) => {
  if (!value) {
    return null
  }

  const date = `${value.year}-${String(value.month).padStart(2, "0")}-${String(value.day).padStart(2, "0")}`

  if (value.hour) {
    return `${date}T${String(value.hour).padStart(2, "0")}:${String(value.minute).padStart(2, "0")}`
  }

  return date
}
const hasScheduleDate = (value) => !!value && value.year && value.month && value.day

const hasTimeOffsetFromUser = (timeZone) => moment().utcOffset() !== moment.tz(moment(), timeZone).utcOffset()

const hasTimeOffsetFromMoment = (dateMoment, timeZone) =>
  moment(dateMoment).utcOffset() !== moment.tz(moment(), timeZone).utcOffset()

function formatSecondsToH(totalSeconds) {
  return Math.floor(totalSeconds / 3600)
}

function formatSecondsToRemainderMM(totalSeconds) {
  const remainderMinutes = Math.floor((totalSeconds % 3600) / 60)
  return `${String(remainderMinutes).padStart(2, "0")}`
}

function formatSecondsToHMM(totalSeconds) {
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  return `${hours}:${String(minutes).padStart(2, "0")}`
}

function formatSecondsToHMMSS(totalSeconds) {
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
}

const isSimpleTimeRangeValid = (startTime, endTime) => {
  if (!startTime || !endTime) return true

  const openMoment = scheduleTimeToMoment(startTime)
  const closeMoment = scheduleTimeToMoment(endTime)

  if (!openMoment || !closeMoment) return true

  // For operating hours, close time must be after open time (same time is invalid)
  return closeMoment.isAfter(openMoment)
}

const formatDuration = (startedAt, endedAt) => {
  if (!startedAt || !endedAt) {
    return null
  }

  const totalSeconds = dayjs(endedAt).diff(dayjs(startedAt), "second")
  const days = Math.floor(totalSeconds / (24 * 3600))
  const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)

  const parts = []
  if (days > 0) parts.push(pluralize("day", days, true))
  if (hours > 0) parts.push(pluralize("hr", hours, true))
  if (minutes > 0) parts.push(pluralize("min", minutes, true))

  return parts.join(", ") || "0 minutes"
}

const getTimeZoneAbbreviation = (date, timeZone) => {
  if (!date || !timeZone) return ""

  return dayjs(date).tz(timeZone).format("z")
}

const getAdaptiveDateFormat = ({
  referenceMoment,
  targetMoment,
  sameDayFormat = "MMM D, h:mm A",
  sameYearFormat = "MMM D",
  differentYearFormat = "MMM D, YYYY",
  showTimeZoneIfHasOffset = false,
  safeTimeZone
} = {}) => {
  if (!referenceMoment || !targetMoment) return differentYearFormat

  const isSameDay = referenceMoment.isSame(targetMoment, "day")
  const isSameYear = referenceMoment.year() === targetMoment.year()

  let format
  if (isSameDay) {
    format = sameDayFormat
  } else if (isSameYear) {
    format = sameYearFormat
  } else {
    format = differentYearFormat
  }

  if (showTimeZoneIfHasOffset && safeTimeZone && hasTimeOffsetFromUser(safeTimeZone)) {
    format += " z"
  }
  return format
}

const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]
const translatedDays = {
  Monday: t`Monday`,
  Tuesday: t`Tuesday`,
  Wednesday: t`Wednesday`,
  Thursday: t`Thursday`,
  Friday: t`Friday`,
  Saturday: t`Saturday`,
  Sunday: t`Sunday`
}

const useDateUtils = () => ({
  hasTime,
  formatTime,
  formatShortDate,
  formatShortTime,
  formatScheduleTime,
  getDay,
  formatScheduleDate,
  formatSecondsToH,
  formatSecondsToRemainderMM,
  formatSecondsToHMM,
  formatSecondsToHMMSS,
  isBeforeToday,
  scheduleDateToMoment,
  momentToScheduleDate,
  momentToScheduleTime,
  isoDateStringToScheduleDate,
  scheduleDateToIsoDateString,
  hasScheduleDate,
  hasTimeOffsetFromUser,
  hasTimeOffsetFromMoment,
  scheduleTimeToMoment,
  isSimpleTimeRangeValid,
  getTimeZoneAbbreviation,
  daysOfWeek,
  translatedDays,
  formatDateTime
})

export {
  useDateUtils,
  getDay,
  hasTime,
  formatDuration,
  formatShortDate,
  formatShortTime,
  formatTime,
  formatScheduleTime,
  formatScheduleDate,
  formatSecondsToH,
  formatSecondsToRemainderMM,
  formatSecondsToHMM,
  formatSecondsToHMMSS,
  isBeforeToday,
  scheduleDateToMoment,
  scheduleDatesMatch,
  momentToScheduleDate,
  momentToScheduleTime,
  isoDateStringToScheduleDate,
  scheduleDateToIsoDateString,
  hasScheduleDate,
  hasTimeOffsetFromUser,
  hasTimeOffsetFromMoment,
  scheduleTimeToMoment,
  isSimpleTimeRangeValid,
  getTimeZoneAbbreviation,
  getAdaptiveDateFormat,
  daysOfWeek,
  translatedDays,
  formatDateTime
}
